import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PlanGatedPage } from "@/components/PlanGatedPage";

const Landing = lazy(() => import("./pages/Landing"));
const HowToUse = lazy(() => import("./pages/HowToUse"));
const Blog = lazy(() => import("./pages/Blog"));
const SignalAI = lazy(() => import("./pages/SignalAI"));
const Settings = lazy(() => import("./pages/Settings"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Network = lazy(() => import("./pages/Network"));
const Thesis = lazy(() => import("./pages/Thesis"));
const Meetings = lazy(() => import("./pages/Meetings"));
const Funds = lazy(() => import("./pages/Funds"));
const Documents = lazy(() => import("./pages/Documents"));
const Reports = lazy(() => import("./pages/Reports"));
const LPPortal = lazy(() => import("./pages/LPPortal"));
const LPLogin = lazy(() => import("./pages/LPLogin"));
const FounderUpdate = lazy(() => import("./pages/FounderUpdate"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

function ThemeInit() {
  useEffect(() => {
    const stored = localStorage.getItem('prysm-theme');
    if (!stored) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.toggle('dark', stored === 'dark');
    }
  }, []);
  return null;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ThemeInit />
        <Toaster />
        <Sonner position="top-right" richColors />
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/how-to-use" element={<HowToUse />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<Blog />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Public routes */}
                <Route path="/lp" element={<LPLogin />} />
                <Route path="/founder-update/:token" element={<FounderUpdate />} />
                
                <Route path="/chat" element={<ProtectedRoute><ErrorBoundary><SignalAI /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/pipeline" element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
                <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
                <Route path="/network" element={<ProtectedRoute><Network /></ProtectedRoute>} />
                <Route path="/thesis" element={<ProtectedRoute><Thesis /></ProtectedRoute>} />
                <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
                
                {/* Pro/Enterprise gated routes */}
                <Route path="/funds" element={
                  <ProtectedRoute>
                    <PlanGatedPage feature="funds" featureLabel="Fund Administration">
                      <Funds />
                    </PlanGatedPage>
                  </ProtectedRoute>
                } />
                <Route path="/documents" element={
                  <ProtectedRoute>
                    <PlanGatedPage feature="documents" featureLabel="Document Intelligence">
                      <Documents />
                    </PlanGatedPage>
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <PlanGatedPage feature="reports" featureLabel="Reporting Engine">
                      <Reports />
                    </PlanGatedPage>
                  </ProtectedRoute>
                } />
                <Route path="/lp-portal" element={
                  <ProtectedRoute>
                    <PlanGatedPage feature="lp-portal" featureLabel="LP Portal">
                      <LPPortal />
                    </PlanGatedPage>
                  </ProtectedRoute>
                } />
                
                {/* Legacy routes */}
                <Route path="/dashboard" element={<Navigate to="/chat" replace />} />
                <Route path="/watchlist" element={<Navigate to="/chat" replace />} />
                <Route path="/alerts" element={<Navigate to="/chat" replace />} />
                <Route path="/research" element={<Navigate to="/chat" replace />} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
